import { useEffect, useRef } from 'react';
import { Link, Navigate, Route, Routes, useLocation, useSearchParams } from 'react-router-dom';
import { CheckIn } from './pages/CheckIn';
import { TeacherLogin } from './pages/TeacherLogin';
import { TeacherHome } from './pages/teacher/TeacherHome';
import { ClassPage } from './pages/teacher/ClassPage';
import { SessionPage } from './pages/teacher/SessionPage';
import { CalibrationPage } from './pages/teacher/CalibrationPage';
import { PlaybookPage } from './pages/teacher/PlaybookPage';
import { ReflectionPage } from './pages/teacher/ReflectionPage';
import { WelfareReportPage } from './pages/head/WelfareReport';
import { CouncilPage, HeadCompliancePage, HeadPlaybookPage } from './pages/head/HeadR2Pages';
import { CounsellorHome } from './pages/counsellor/CounsellorHome';
import { SchoolAdminCompliance, TrustPage } from './pages/compliance/CompliancePages';
import { CouncilView } from './pages/public/CouncilView';
import { PlatformCompliancePage } from './pages/admin/R2Admin';
import { AdminInsightsPage } from './pages/admin/R3Admin';
import { CalendarPage, SchoolAdminCalendarPage, StaffViewPage, WhatWorksPage } from './pages/head/HeadR3Pages';
import { MyCheckinPage } from './pages/staff/MyCheckin';
import { StaffNotice } from './pages/StaffNotice';
import { MunicipalityHome } from './pages/municipality/MunicipalityHome';
import { MethodPage, PublicReportsPage } from './pages/public/R3Public';
import { HeadActivity, HeadHome, HeadResults } from './pages/head/HeadPages';
import { HeadSettingsPage } from './pages/head/HeadSettings';
import { HeadQuestionsPage } from './pages/head/HeadR5Pages';
import { QuestionsPage } from './pages/school_admin/QuestionsPage';
import { AdminOverview } from './pages/admin/AdminOverview';
import { AdminSchools } from './pages/admin/AdminSchools';
import { AdminAddSchool } from './pages/admin/AdminAddSchool';
import { SchoolPage } from './pages/admin/SchoolPage';
import { PersonPage } from './pages/admin/SchoolStaff';
import { AdminApprovals, AdminArchive, AdminLog, AdminSettings } from './pages/admin/AdminPlatform';
import { ContentPage } from './pages/admin/ContentPage';
import { Notifications } from './pages/Notifications';
import { AccessibilityStatement } from './pages/AccessibilityStatement';
import { ChooseRole } from './pages/ChooseRole';
import { NoRoles, RoleHome } from './pages/RoleHome';
import { TwoFactor } from './pages/TwoFactor';
import { SetPassword } from './pages/SetPassword';
import { I18nProvider, useI18n } from './i18n';
import { AnnouncerProvider, useDocumentTitle } from './ui/a11y';
import { markNavigation } from './ui/focus';

export function App() {
  return (
    <I18nProvider>
      <AnnouncerProvider>
        <RouteFocus />
        <Routes>
          {/* Student path: no auth, no layout chrome, nothing but the check-in. */}
          <Route path="/c/:code" element={<CheckIn />} />

          {/* Staff paths: behind Supabase Auth. One shared login; where it sends
              you after signing in depends on the role this sign-in is acting
              as (my_context / set_active_role, 0006_v2_foundations.sql). */}
          <Route path="/teacher/login" element={<TeacherLogin />} />
          <Route path="/teacher" element={<TeacherHome />} />
          {/* Teacher check-in workflow (Workflow I, 0017). */}
          <Route path="/teacher/class/:classId" element={<ClassPage />} />
          <Route path="/teacher/session/:sessionId" element={<SessionPage />} />
          <Route path="/teacher/calibration" element={<CalibrationPage />} />
          {/* 0026's frontend (spec §12.G): the teacher's monthly STARL loop. */}
          <Route path="/teacher/class/:classId/reflection" element={<ReflectionPage />} />
          <Route path="/teacher/playbook" element={<PlaybookPage />} />
          {/* Head of school (Workflow H, 0015). */}
          <Route path="/school" element={<HeadHome />} />
          <Route path="/school/results" element={<HeadResults />} />
          <Route path="/school/activity" element={<HeadActivity />} />
          <Route path="/school/report" element={<WelfareReportPage />} />
          <Route path="/school/council" element={<CouncilPage />} />
          <Route path="/school/playbook" element={<HeadPlaybookPage />} />
          <Route path="/school/compliance" element={<HeadCompliancePage />} />
          <Route path="/school/settings" element={<HeadSettingsPage />} />
          {/* Phase 4 of the topic & follow-up check-in revision (0028): the
              head's approval queue for a school admin's custom questions. */}
          <Route path="/school/questions" element={<HeadQuestionsPage />} />
          {/* Release 3 (0019): What works, staff and students, calendar. */}
          <Route path="/school/what-works" element={<WhatWorksPage />} />
          <Route path="/school/staff" element={<StaffViewPage />} />
          <Route path="/school/calendar" element={<CalendarPage />} />
          <Route path="/calendar" element={<SchoolAdminCalendarPage />} />
          {/* Every school-side role: the staff notice and the own check-in. */}
          <Route path="/notice" element={<StaffNotice />} />
          <Route path="/me/checkin" element={<MyCheckinPage />} />
          {/* Municipality coordinator. */}
          <Route path="/municipality" element={<MunicipalityHome />} />
          {/* Counsellor, school admin's compliance pack (0018). */}
          <Route path="/counsellor" element={<CounsellorHome />} />
          <Route path="/compliance" element={<SchoolAdminCompliance />} />
          {/* School admin's own custom question bank (0028, spec §12.B). */}
          <Route path="/questions" element={<QuestionsPage />} />
          {/* Public, no sign-in: the council link and the trust page (0018). */}
          <Route path="/council/:token" element={<CouncilView />} />
          <Route path="/trust" element={<TrustPage />} />
          <Route path="/privacy-method" element={<MethodPage />} />
          <Route path="/insights" element={<PublicReportsPage />} />
          <Route path="/admin" element={<AdminOverview />} />
          <Route path="/admin/schools" element={<AdminSchools />} />
          <Route path="/admin/schools/new" element={<AdminAddSchool />} />
          <Route path="/admin/schools/:id" element={<SchoolPage />} />
          <Route path="/admin/schools/:id/people/:uid" element={<PersonPage />} />
          <Route path="/admin/schools/:id/:tab" element={<SchoolPage />} />
          <Route path="/admin/content" element={<ContentPage />} />
          <Route path="/admin/content/:tab" element={<ContentPage />} />
          <Route path="/admin/approvals" element={<AdminApprovals />} />
          <Route path="/admin/log" element={<AdminLog />} />
          <Route path="/admin/archive" element={<AdminArchive />} />
          <Route path="/admin/settings" element={<AdminSettings />} />
          <Route path="/admin/compliance" element={<PlatformCompliancePage />} />
          <Route path="/admin/insights" element={<AdminInsightsPage />} />
          {/* No all-schools staff list: people are seen and managed inside one
              school, in support mode (spec G1; 0010). Old links go to Schools. */}
          <Route path="/admin/staff" element={<Navigate to="/admin/schools" replace />} />
          <Route path="/admin/staff/new" element={<LegacyAddStaff />} />
          <Route path="/notifications" element={<Notifications />} />
          <Route path="/home" element={<RoleHome />} />
          <Route path="/choose-role" element={<ChooseRole />} />
          <Route path="/no-roles" element={<NoRoles />} />
          <Route path="/mfa" element={<TwoFactor />} />
          <Route path="/set-password" element={<SetPassword />} />

          {/* Accessibility statement (Act 306/2019), reachable from every view. */}
          <Route path="/accessibility" element={<AccessibilityStatement />} />
          <Route path="/saavutettavuus" element={<Navigate to="/accessibility" replace />} />
          <Route path="/tillganglighet" element={<Navigate to="/accessibility" replace />} />

          <Route path="/" element={<Navigate to="/teacher/login" replace />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </AnnouncerProvider>
    </I18nProvider>
  );
}

// Marks in-app navigations (not the first load) so the next page moves focus
// to its <h1> — see ui/focus.ts.
function RouteFocus() {
  const { pathname } = useLocation();
  const first = useRef(true);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    markNavigation();
  }, [pathname]);
  return null;
}

function LegacyAddStaff() {
  const [params] = useSearchParams();
  const school = params.get('school');
  return <Navigate to={school ? `/admin/schools/${school}/staff` : '/admin/schools'} replace />;
}

function NotFound() {
  const { t } = useI18n();
  useDocumentTitle(t['notFound.title']);
  return (
    <main className="not-found">
      <div>
        <h1>{t['notFound.title']}</h1>
        <p className="text-muted">{t['notFound.body']}</p>
        <p>
          <Link to="/teacher/login">{t['notFound.staffLink']}</Link>
        </p>
      </div>
    </main>
  );
}
